import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useRef, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { Transition } from 'react-transition-group';
import { LoadingButton as Button } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { CheckboxField, Field, FormFooter } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Stack, Text } from '@atlaskit/primitives';
import Textarea from '@atlaskit/textarea';
import FeedbackScoreButtons from './FeedbackScoreButtons';
var getExpandedHeight = function getExpandedHeight(ref, state) {
  if (!ref.current) {
    return '0';
  }
  switch (state) {
    case 'entering':
      return "".concat(ref.current.scrollHeight, "px");
    case 'entered':
      // needed for TextField auto height expand
      return "none";
    default:
      return '0';
  }
};
var transitionDuration = 200;
export default (function (_ref) {
  var question = _ref.question,
    statement = _ref.statement,
    textPlaceholder = _ref.textPlaceholder,
    textLabel = _ref.textLabel,
    onSubmit = _ref.onSubmit;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    expanded = _useState2[0],
    setExpanded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    canContactDefault = _useState4[0],
    setCanContactDefault = _useState4[1];
  var hasAutoFilledCanContactRef = useRef(false);
  var expandedAreaRef = useRef(null);
  var onScoreSelect = useCallback(function () {
    setExpanded(true);
  }, [setExpanded]);

  // On the first type the user types some feedback we auto select
  // the option for allowing feedback. This automatic selection only
  // happens once. After that it is up to the user to control
  var onFeedbackChange = useCallback(function () {
    if (hasAutoFilledCanContactRef.current) {
      return;
    }
    hasAutoFilledCanContactRef.current = true;
    setCanContactDefault(true);
  }, []);
  return jsx("section", {
    "aria-labelledby": "contextualSurveyQuestion"
  }, jsx(Stack, {
    space: "space.150"
  }, jsx(Heading, {
    id: "contextualSurveyQuestion",
    size: "xsmall"
  }, question), statement && jsx(Text, {
    as: "p",
    id: "contextualSurveyStatement"
  }, statement)), jsx(Form, {
    onSubmit: onSubmit
  }, function (_ref2) {
    var formProps = _ref2.formProps,
      submitting = _ref2.submitting;
    return jsx("form", formProps, jsx(Field, {
      name: "feedbackScore",
      isDisabled: submitting,
      isRequired: true
    }, function (_ref3) {
      var fieldProps = _ref3.fieldProps;
      return jsx(FeedbackScoreButtons, _extends({}, fieldProps, {
        onChange: function onChange(score) {
          fieldProps.onChange(score);
          onScoreSelect();
        }
      }));
    }), jsx(Transition, {
      in: expanded,
      timeout: transitionDuration,
      mountOnEnter: true
    }, function (state) {
      return jsx("div", {
        // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
        css: css({
          transition: "max-height ".concat(transitionDuration, "ms ease-in-out"),
          overflow: 'hidden',
          // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
          maxHeight: getExpandedHeight(expandedAreaRef, state)
        }),
        ref: expandedAreaRef
      }, jsx(Field, {
        name: "writtenFeedback",
        defaultValue: "",
        isDisabled: submitting
      }, function (_ref4) {
        var fieldProps = _ref4.fieldProps;
        return jsx(Textarea, _extends({}, fieldProps, {
          "aria-label": textLabel,
          placeholder: textPlaceholder,
          onChange: function onChange(event) {
            fieldProps.onChange(event);
            onFeedbackChange();
          }
        }));
      }), jsx(CheckboxField, {
        name: "canContact",
        isDisabled: submitting,
        defaultIsChecked: canContactDefault
      }, function (_ref5) {
        var fieldProps = _ref5.fieldProps;
        return jsx(Checkbox, _extends({}, fieldProps, {
          label: "Atlassian can contact me about this feedback"
        }));
      }), jsx(FormFooter, null, jsx(Button, {
        type: "submit",
        appearance: "primary",
        isLoading: submitting
      }, "Submit")));
    }));
  }));
});